import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Bell,
  BookOpen,
  Bot,
  BrainCircuit,
  Building2,
  CheckCircle2,
  ChevronDown,
  CircleGauge,
  ClipboardCheck,
  Database,
  FileCheck2,
  FileText,
  Fingerprint,
  Globe2,
  History,
  KeyRound,
  Library,
  LockKeyhole,
  MessageSquareText,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  RefreshCw,
  Search,
  Send,
  Settings2,
  ShieldCheck,
  Sparkles,
  UploadCloud,
  UsersRound,
  X,
} from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CoreApi,
  CoreApiError,
  type AccessIdentity,
  type ActionRecord,
  type AuditRecord,
  type ConversationRecord,
  type EvaluationRecord,
  type EventRecord,
  type GroundedResponse,
  type InsightRecord,
  type KnowledgeRecord,
  type Role,
  type RuleRecord,
  type SourceRecord,
} from "./coreApi";

type View =
  | "overview"
  | "library"
  | "sources"
  | "governance"
  | "isolation"
  | "permissions"
  | "provider"
  | "rules"
  | "audit"
  | "integration"
  | "evaluations"
  | "events"
  | "insights"
  | "actions"
  | "assistant";
type Modal = "knowledge" | "source" | "edit" | null;
type Locale = "en" | "ar";

const api = new CoreApi();
const roles: Array<{
  id: Role;
  user: "1" | "2" | "3" | "4";
  label: string;
  labelAr: string;
  subtitle: string;
  subtitleAr: string;
  initials: string;
}> = [
  {
    id: "synergize_team",
    user: "1",
    label: "SYNERGIZE Team",
    labelAr: "فريق سينرجيز",
    subtitle: "Knowledge authority",
    subtitleAr: "جهة إدارة المعرفة",
    initials: "SA",
  },
  {
    id: "consultant",
    user: "2",
    label: "Consultant",
    labelAr: "مستشار",
    subtitle: "Assigned advisor",
    subtitleAr: "مستشار مكلّف",
    initials: "TG",
  },
  {
    id: "leadership",
    user: "3",
    label: "Leadership",
    labelAr: "القيادة",
    subtitle: "Decision authority",
    subtitleAr: "جهة اتخاذ القرار",
    initials: "FA",
  },
  {
    id: "employee",
    user: "4",
    label: "Employee",
    labelAr: "موظف",
    subtitle: "Authorized contributor",
    subtitleAr: "مساهم مخوّل",
    initials: "NK",
  },
];
const organizations = [
  {
    id: "1",
    name: "Synergize Software (TechCo)",
    nameAr: "سينرجيز للبرمجيات (تك كو)",
  },
  { id: "2", name: "Vanguard Precision", nameAr: "فانغارد بريسيجن" },
  { id: "3", name: "Apex Software Co.", nameAr: "أبيكس للبرمجيات" },
];
const nav: Array<{
  id: View;
  label: string;
  labelAr: string;
  icon: typeof BrainCircuit;
  group: "Workspace" | "Governance" | "Intelligence";
  groupAr: string;
  admin?: boolean;
}> = [
  {
    id: "overview",
    label: "Intelligence Dashboard",
    labelAr: "لوحة الذكاء",
    icon: BrainCircuit,
    group: "Workspace",
    groupAr: "مساحة العمل",
  },
  {
    id: "library",
    label: "Knowledge Library",
    labelAr: "مكتبة المعرفة",
    icon: Library,
    group: "Workspace",
    groupAr: "مساحة العمل",
    admin: true,
  },
  {
    id: "sources",
    label: "Source Ingestion",
    labelAr: "إدارة المصادر",
    icon: UploadCloud,
    group: "Workspace",
    groupAr: "مساحة العمل",
    admin: true,
  },
  {
    id: "governance",
    label: "Governance Queue",
    labelAr: "قائمة الحوكمة",
    icon: ClipboardCheck,
    group: "Workspace",
    groupAr: "مساحة العمل",
    admin: true,
  },
  {
    id: "isolation",
    label: "Organization Isolation",
    labelAr: "عزل المؤسسات",
    icon: LockKeyhole,
    group: "Governance",
    groupAr: "الحوكمة",
    admin: true,
  },
  {
    id: "permissions",
    label: "Roles & Permissions",
    labelAr: "الأدوار والصلاحيات",
    icon: ShieldCheck,
    group: "Governance",
    groupAr: "الحوكمة",
    admin: true,
  },
  {
    id: "provider",
    label: "Provider Connection",
    labelAr: "اتصال المزوّد",
    icon: KeyRound,
    group: "Governance",
    groupAr: "الحوكمة",
    admin: true,
  },
  {
    id: "rules",
    label: "AI Rules",
    labelAr: "قواعد الذكاء",
    icon: Settings2,
    group: "Governance",
    groupAr: "الحوكمة",
    admin: true,
  },
  {
    id: "evaluations",
    label: "Evaluation Center",
    labelAr: "مركز التقييم",
    icon: FileCheck2,
    group: "Governance",
    groupAr: "الحوكمة",
    admin: true,
  },
  {
    id: "audit",
    label: "Audit Log",
    labelAr: "سجل التدقيق",
    icon: History,
    group: "Governance",
    groupAr: "الحوكمة",
    admin: true,
  },
  {
    id: "integration",
    label: "NASAQ Integration",
    labelAr: "تكامل نسق",
    icon: Database,
    group: "Intelligence",
    groupAr: "الذكاء",
    admin: true,
  },
  {
    id: "events",
    label: "Event Monitor",
    labelAr: "مراقبة الأحداث",
    icon: Activity,
    group: "Intelligence",
    groupAr: "الذكاء",
    admin: true,
  },
  {
    id: "insights",
    label: "Proactive Insights",
    labelAr: "الرؤى الاستباقية",
    icon: Bell,
    group: "Intelligence",
    groupAr: "الذكاء",
  },
  {
    id: "actions",
    label: "Action Approvals",
    labelAr: "اعتمادات الإجراءات",
    icon: CheckCircle2,
    group: "Intelligence",
    groupAr: "الذكاء",
  },
  {
    id: "assistant",
    label: "Assistant Testing Lab",
    labelAr: "مختبر المساعد",
    icon: Sparkles,
    group: "Intelligence",
    groupAr: "الذكاء",
  },
];

const pageIcons: Record<string, typeof BrainCircuit> = {
  "Knowledge Library": Library,
  "Source Ingestion": UploadCloud,
  "Review and Governance Queue": ClipboardCheck,
  "AI Provider Connection": KeyRound,
  "AI Rules": Settings2,
  "Append-only Audit Log": History,
  "Organization Isolation": LockKeyhole,
  "Roles and Permissions": ShieldCheck,
  "Event Monitor": Activity,
  "Proactive Insights Queue": Bell,
  "Action Approval Queue": CheckCircle2,
  "Evaluation Center": FileCheck2,
  "NASAQ AI Assistant": Sparkles,
};

const LocaleContext = createContext<Locale>("en");
const arabicCopy: Record<string, string> = {
  "Governed organizational memory": "ذاكرة مؤسسية محكومة",
  "Knowledge Library": "مكتبة المعرفة",
  "Only published, scope-authorized records are retrievable by assistants.":
    "لا يسترجع المساعد إلا السجلات المنشورة والمصرّح بها ضمن النطاق.",
  "Secure ingestion pipeline": "مسار إدخال آمن",
  "Source Ingestion": "إدارة المصادر",
  "Original files, checksums, extraction, embeddings and failures are processed by the Core server.":
    "يعالج خادم النواة الملفات الأصلية والبصمات الرقمية والاستخراج والفهرسة وحالات الإخفاق.",
  "Human authority boundary": "حدود السلطة البشرية",
  "Review and Governance Queue": "قائمة المراجعة والحوكمة",
  "Drafts require expert review, approval and publication before retrieval.":
    "تتطلب المسودات مراجعة خبير واعتماداً ونشراً قبل إتاحتها للاسترجاع.",
  "Server-only provider boundary": "حدود المزوّد على الخادم",
  "AI Provider Connection": "اتصال مزوّد الذكاء الاصطناعي",
  "Gemini credentials remain server-side and are never returned to this interface.":
    "تبقى بيانات اعتماد Gemini على الخادم ولا تُعرض أبداً في هذه الواجهة.",
  "Governed instruction layer": "طبقة تعليمات محكومة",
  "AI Rules": "قواعد الذكاء الاصطناعي",
  "Published rules constrain retrieval and proposed actions for every role.":
    "تضبط القواعد المنشورة الاسترجاع والإجراءات المقترحة لكل دور.",
  "Traceable control plane": "طبقة تحكم قابلة للتتبع",
  "Append-only Audit Log": "سجل تدقيق غير قابل للمحو",
  "Actions are persisted with verified actor, role, entity, organization and request trace.":
    "تُحفظ الإجراءات مع هوية المنفذ والدور والكيان والمؤسسة ومسار الطلب.",
  "Server-verified access boundary": "حدود وصول موثقة من الخادم",
  "Organization Isolation": "عزل المؤسسات",
  "Roles and Permissions": "الأدوار والصلاحيات",
  "The synchronized identity map defines organization and department retrieval scope.":
    "تحدد خريطة الهوية المتزامنة نطاق الاسترجاع للمؤسسة والقسم.",
  "Administration remains exclusive to SYNERGIZE; consumption permissions are role-aware and server enforced.":
    "تبقى الإدارة حصرياً لفريق سينرجيز، وتُفرض صلاحيات الاستخدام حسب الدور من الخادم.",
  "Additive NASAQ event bridge": "جسر أحداث نسق",
  "Event Monitor": "مراقبة الأحداث",
  "Only whitelisted operational metadata is persisted; events are idempotent, scoped and processed by deterministic monitors.":
    "لا تُحفظ إلا البيانات التشغيلية المسموح بها، وتُعالج الأحداث ضمن نطاقها دون تكرار.",
  "Authorized third-eye monitoring": "مراقبة استباقية مخوّلة",
  "Proactive Insights Queue": "قائمة الرؤى الاستباقية",
  "Event-driven alerts are deduplicated, scoped and persisted across devices.":
    "تُمنع ازدواجية التنبيهات المرتبطة بالأحداث وتُحفظ ضمن نطاقها عبر الأجهزة.",
  "Human approval boundary": "حدود الاعتماد البشري",
  "Action Approval Queue": "قائمة اعتماد الإجراءات",
  "Assistant proposals remain drafts until an authorized human decides them. Approval records the decision but never mutates NASAQ workflows directly.":
    "تبقى مقترحات المساعد مسودات حتى يقررها شخص مخوّل، ويُسجل الاعتماد القرار دون تعديل سير العمل مباشرة.",
  "Continuous quality control": "ضبط جودة مستمر",
  "Evaluation Center": "مركز التقييم",
  "Every assistant response records a persistent citation-integrity result without storing the user prompt in evaluation details.":
    "يسجل كل رد نتيجة دائمة لسلامة الاستشهاد دون حفظ نص المستخدم ضمن تفاصيل التقييم.",
  "Verified user · scoped organization": "مستخدم موثّق · مؤسسة محددة النطاق",
  "NASAQ AI Assistant": "مساعد نسق الذكي",
  "Real retrieval, citations, evidence sufficiency and approval-controlled proposals.":
    "استرجاع واستشهادات فعلية، وكفاية أدلة، ومقترحات خاضعة للاعتماد.",
  "NASAQ INTELLIGENCE CORE": "نواة ذكاء نسق",
  "Governed intelligence, grounded in evidence.":
    "ذكاء محكوم، مستند إلى الأدلة.",
  "Persistent knowledge, verified organizational context, controlled AI actions and complete audit traceability.":
    "معرفة مستمرة، وسياق مؤسسي موثق، وإجراءات ذكاء منضبطة، وقابلية تدقيق كاملة.",
  "Open Assistant": "فتح المساعد",
  "Knowledge Studio": "استوديو المعرفة",
  "Core status": "حالة النواة",
  Operational: "تعمل بكفاءة",
  "Persistent local service": "خدمة محلية مستمرة",
};
function useLocale() {
  return useContext(LocaleContext);
}
function localize(value: string, locale: Locale) {
  return locale === "ar" ? arabicCopy[value] || value : value;
}

function badge(value: string) {
  return value.toLowerCase().replace(/\s+/g, "-");
}
function formatDate(value?: string | null) {
  return value
    ? new Intl.DateTimeFormat("en", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "Not set";
}

function Header({
  role,
  setRole,
  organizationId,
  setOrganizationId,
  search,
  setSearch,
  openSearch,
  refresh,
  busy,
  locale,
  setLocale,
  insightCount,
}: {
  role: Role;
  setRole: (role: Role) => void;
  organizationId: string;
  setOrganizationId: (id: string) => void;
  search: string;
  setSearch: (value: string) => void;
  openSearch: () => void;
  refresh: () => void;
  busy: boolean;
  locale: Locale;
  setLocale: (locale: Locale) => void;
  insightCount: number;
}) {
  const profile = roles.find((item) => item.id === role)!;
  const availableOrganizations =
    role === "synergize_team"
      ? organizations
      : organizations.filter((org) => org.id === organizationId);
  return (
    <header className="topbar">
      <div className="global-search">
        <Search />
        <input
          id="core-global-search"
          aria-label={locale === "ar" ? "البحث في نواة الذكاء" : "Search Core"}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            if (event.target.value.trim()) openSearch();
          }}
          placeholder={
            locale === "ar"
              ? "ابحث في نواة ذكاء نسق..."
              : "Search NASAQ Intelligence Core..."
          }
        />
        <kbd>⌘ K</kbd>
      </div>
      <div className="topbar-actions">
        <label className="organization-switch">
          <Building2 />
          <span>{locale === "ar" ? "المؤسسة" : "Organization"}</span>
          <select
            aria-label={
              locale === "ar" ? "المؤسسة النشطة" : "Active organization"
            }
            value={organizationId}
            onChange={(event) => setOrganizationId(event.target.value)}
          >
            {availableOrganizations.map((org) => (
              <option key={org.id} value={org.id}>
                {locale === "ar" ? org.nameAr : org.name}
              </option>
            ))}
          </select>
          <ChevronDown />
        </label>
        <label className="language-switch">
          <Globe2 />
          <select
            aria-label={locale === "ar" ? "اللغة" : "Language"}
            value={locale}
            onChange={(event) => setLocale(event.target.value as Locale)}
          >
            <option value="en">EN</option>
            <option value="ar">AR</option>
          </select>
          <ChevronDown />
        </label>
        <button
          className="topbar-icon"
          aria-label={locale === "ar" ? "الإشعارات" : "Notifications"}
          onClick={() => openSearch()}
        >
          <Bell />
          {insightCount > 0 && <span>{insightCount}</span>}
        </button>
        <button
          className="topbar-icon"
          aria-label={locale === "ar" ? "تحديث البيانات" : "Refresh data"}
          onClick={refresh}
          disabled={busy}
        >
          <RefreshCw className={busy ? "spin" : ""} />
        </button>
        <label className="profile-switch">
          <span>{profile.initials}</span>
          <div>
            <strong>{locale === "ar" ? profile.labelAr : profile.label}</strong>
            <small>
              {locale === "ar" ? profile.subtitleAr : profile.subtitle}
            </small>
          </div>
          <select
            aria-label={locale === "ar" ? "الدور التجريبي" : "Demo role"}
            value={role}
            onChange={(event) => setRole(event.target.value as Role)}
          >
            {roles.map((item) => (
              <option key={item.id} value={item.id}>
                {locale === "ar" ? item.labelAr : item.label}
              </option>
            ))}
          </select>
          <ChevronDown />
        </label>
      </div>
    </header>
  );
}

function Sidebar({
  view,
  setView,
  role,
  collapsed,
  setCollapsed,
  locale,
}: {
  view: View;
  setView: (view: View) => void;
  role: Role;
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  locale: Locale;
}) {
  const visible =
    role === "synergize_team"
      ? nav
      : nav.filter((item) => item.id === "assistant");
  return (
    <aside className={`core-sidebar ${collapsed ? "collapsed" : ""}`}>
      <div className="brand-mark">
        <span className="nasaq-bars" aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
          <i />
        </span>
        <div>
          <strong>{locale === "ar" ? "نسق" : "NASAQ"}</strong>
          <span>{locale === "ar" ? "نواة الذكاء" : "INTELLIGENCE CORE"}</span>
        </div>
      </div>
      <nav
        aria-label={locale === "ar" ? "التنقل الرئيسي" : "Primary navigation"}
      >
        {visible.map(
          ({ id, label, labelAr, icon: Icon, group, groupAr }, index) => {
            const itemLabel = locale === "ar" ? labelAr : label;
            return (
              <div className="nav-entry" key={id}>
                {(index === 0 || visible[index - 1]?.group !== group) && (
                  <span className="nav-group">
                    {locale === "ar" ? groupAr : group}
                  </span>
                )}
                <button
                  title={itemLabel}
                  className={view === id ? "active" : ""}
                  onClick={() => setView(id)}
                  aria-label={itemLabel}
                  aria-current={view === id ? "page" : undefined}
                >
                  <Icon />
                  <span>{itemLabel}</span>
                </button>
              </div>
            );
          },
        )}
      </nav>
      <button
        className="sidebar-toggle"
        onClick={() => setCollapsed(!collapsed)}
        aria-label={
          collapsed
            ? locale === "ar"
              ? "توسيع القائمة"
              : "Expand navigation"
            : locale === "ar"
              ? "طي القائمة"
              : "Collapse navigation"
        }
      >
        {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
        <span>{locale === "ar" ? "طي القائمة" : "Collapse"}</span>
      </button>
    </aside>
  );
}

function PageHeading({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  const locale = useLocale();
  const Icon = pageIcons[title] || BrainCircuit;
  return (
    <div className="view-heading">
      <div className="heading-copy">
        <span className="heading-icon">
          <Icon />
        </span>
        <div>
          <span>{localize(eyebrow, locale)}</span>
          <h2>{localize(title, locale)}</h2>
          <p>{localize(description, locale)}</p>
        </div>
      </div>
      {action && <div className="heading-action">{action}</div>}
    </div>
  );
}

function Empty({
  icon: Icon = Database,
  title,
  text,
}: {
  icon?: typeof Database;
  title: string;
  text: string;
}) {
  return (
    <div className="empty-state">
      <Icon />
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}
function Notice({ error, clear }: { error: string; clear: () => void }) {
  return (
    <div className="notice error-notice" role="alert">
      <AlertTriangle />
      <div>
        <strong>Core request could not complete</strong>
        <span>{error}</span>
      </div>
      <button
        className="icon-button"
        onClick={clear}
        aria-label="Dismiss error"
      >
        <X />
      </button>
    </div>
  );
}

function Overview({
  metrics,
  knowledge,
  insights,
  setView,
}: {
  metrics: Record<string, number>;
  knowledge: KnowledgeRecord[];
  insights: InsightRecord[];
  setView: (view: View) => void;
}) {
  const locale = useLocale();
  const items = [
    ["knowledge_records", "Knowledge records", Library],
    ["published_knowledge", "Published", BookOpen],
    ["source_files", "Source files", UploadCloud],
    ["active_conversations", "Conversations", MessageSquareText],
    ["new_insights", "Active insights", Bell],
    ["audit_events", "Audit events", History],
  ] as const;
  return (
    <>
      <section className="hero">
        <div>
          <span>{localize("NASAQ INTELLIGENCE CORE", locale)}</span>
          <h1>
            {localize("Governed intelligence, grounded in evidence.", locale)}
          </h1>
          <p>
            {localize(
              "Persistent knowledge, verified organizational context, controlled AI actions and complete audit traceability.",
              locale,
            )}
          </p>
          <div className="hero-actions">
            <button
              className="primary-button"
              onClick={() => setView("assistant")}
            >
              <Sparkles /> {localize("Open Assistant", locale)}
            </button>
            <button
              className="secondary-button"
              onClick={() => setView("library")}
            >
              <Library /> {localize("Knowledge Studio", locale)}
            </button>
          </div>
        </div>
        <div className="health-card">
          <CircleGauge />
          <span>{localize("Core status", locale)}</span>
          <strong>{localize("Operational", locale)}</strong>
          <small>{localize("Persistent local service", locale)}</small>
        </div>
      </section>
      <section className="metric-grid">
        {items.map(([key, label, Icon]) => (
          <article className="metric-card" key={key}>
            <Icon />
            <div>
              <strong>{metrics[key] ?? 0}</strong>
              <span>{label}</span>
              <small>
                {locale === "ar"
                  ? "قيمة مباشرة من الواجهة البرمجية"
                  : "Live API value"}
              </small>
            </div>
          </article>
        ))}
      </section>
      <section className="dashboard-grid">
        <div className="panel wide-panel">
          <div className="panel-header">
            <div>
              <h3>
                <BookOpen />{" "}
                {locale === "ar"
                  ? "أحدث المعرفة المحكومة"
                  : "Recent governed knowledge"}
              </h3>
              <p>
                {locale === "ar"
                  ? "السجلات المحفوظة الحالية وحالات دورة حياتها."
                  : "Current persisted records and lifecycle states."}
              </p>
            </div>
            <button className="text-button" onClick={() => setView("library")}>
              {locale === "ar" ? "عرض الكل" : "View all"} <ArrowRight />
            </button>
          </div>
          {knowledge.length ? (
            <div className="record-table">
              <div className="table-row table-head">
                <span>{locale === "ar" ? "العنوان" : "Title"}</span>
                <span>{locale === "ar" ? "الفئة" : "Category"}</span>
                <span>{locale === "ar" ? "النطاق" : "Scope"}</span>
                <span>{locale === "ar" ? "الحالة" : "Status"}</span>
                <span>{locale === "ar" ? "محدّث" : "Updated"}</span>
                <span />
                <span />
              </div>
              {knowledge.slice(0, 5).map((item) => (
                <button
                  className="table-row"
                  key={item.id}
                  onClick={() => setView("library")}
                >
                  <span>
                    <BookOpen />
                    {item.title}
                    <small>{item.code}</small>
                  </span>
                  <span>{item.category}</span>
                  <span>{item.organization_id}</span>
                  <span>
                    <i className={`status-pill ${badge(item.lifecycle)}`}>
                      {item.lifecycle}
                    </i>
                  </span>
                  <span>{formatDate(item.updated_at)}</span>
                  <span />
                  <span>
                    <ArrowRight />
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <Empty
              title="No knowledge yet"
              text="Create a governed manual record or ingest a source."
            />
          )}
        </div>
        <aside className="panel activity-panel">
          <div className="panel-header">
            <div>
              <h3>
                <Bell />{" "}
                {locale === "ar" ? "الرؤى ذات الأولوية" : "Priority insights"}
              </h3>
              <p>
                {locale === "ar"
                  ? "إرشاد مخوّل تحركه الأحداث."
                  : "Authorized event-driven guidance."}
              </p>
            </div>
            <button className="text-button" onClick={() => setView("insights")}>
              {locale === "ar" ? "فتح القائمة" : "Open queue"}
            </button>
          </div>
          {insights.length ? (
            <div className="timeline">
              {insights.slice(0, 5).map((item) => (
                <button key={item.id} onClick={() => setView("insights")}>
                  <i />
                  <div>
                    <strong>{item.title}</strong>
                    <span>{item.message}</span>
                  </div>
                  <small>{item.priority}</small>
                </button>
              ))}
            </div>
          ) : (
            <Empty
              icon={Bell}
              title="No active insights"
              text="Authorized NASAQ events will appear here after evaluation."
            />
          )}
        </aside>
      </section>
    </>
  );
}

function KnowledgeView({
  records,
  search,
  transition,
  openCreate,
  edit,
}: {
  records: KnowledgeRecord[];
  search: string;
  transition: (item: KnowledgeRecord) => void;
  openCreate: () => void;
  edit: (item: KnowledgeRecord) => void;
}) {
  const [filter, setFilter] = useState("All");
  const filtered = records.filter(
    (item) =>
      (filter === "All" || item.lifecycle === filter) &&
      `${item.title} ${item.code} ${item.tags.join(" ")}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  return (
    <section className="panel page-panel">
      <PageHeading
        eyebrow="Governed organizational memory"
        title="Knowledge Library"
        description="Only published, scope-authorized records are retrievable by assistants."
        action={
          <button className="primary-button" onClick={openCreate}>
            <Plus /> New knowledge
          </button>
        }
      />
      <div className="filter-row">
        {["All", "Draft", "Review", "Approved", "Published", "Retired"].map(
          (item) => (
            <button
              key={item}
              className={filter === item ? "active" : ""}
              onClick={() => setFilter(item)}
            >
              {item}
            </button>
          ),
        )}
      </div>
      {filtered.length ? (
        <div className="knowledge-cards">
          {filtered.map((item) => (
            <article className="knowledge-card" key={item.id}>
              <div className="knowledge-card-top">
                <div className="file-icon">
                  <BookOpen />
                </div>
                <div>
                  <span>
                    {item.code} · v{item.current_version}
                  </span>
                  <h3>{item.title}</h3>
                </div>
                <i className={`status-pill ${badge(item.lifecycle)}`}>
                  {item.lifecycle}
                </i>
              </div>
              <p>{item.content || item.applicability}</p>
              <div className="tag-row">
                {item.tags.map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
              <footer>
                <span>
                  <Fingerprint /> {item.organization_id}
                </span>
                <span>{item.confidentiality}</span>
                <span>{formatDate(item.updated_at)}</span>
              </footer>
              <div className="review-actions">
                <button className="secondary-button" onClick={() => edit(item)}>
                  <FileText /> Create new version
                </button>
                {item.lifecycle !== "Retired" && (
                  <button
                    className="secondary-button"
                    onClick={() => transition(item)}
                  >
                    <ArrowRight /> Advance governed state
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          icon={Search}
          title="No matching knowledge"
          text="Adjust the search or lifecycle filter."
        />
      )}
    </section>
  );
}

function SourcesView({
  sources,
  jobs,
  upload,
}: {
  sources: SourceRecord[];
  jobs: Array<Record<string, unknown>>;
  upload: () => void;
}) {
  return (
    <section className="panel page-panel">
      <PageHeading
        eyebrow="Secure ingestion pipeline"
        title="Source Ingestion"
        description="Original files, checksums, extraction, embeddings and failures are processed by the Core server."
        action={
          <button className="primary-button" onClick={upload}>
            <UploadCloud /> Upload source
          </button>
        }
      />
      <div className="pipeline">
        {[
          "Secure upload",
          "Metadata",
          "Validation",
          "Extraction",
          "Checksum",
          "Chunking",
          "Embeddings",
          "Review",
        ].map((item, index) => (
          <div key={item}>
            <b>{index + 1}</b>
            <span>{item}</span>
          </div>
        ))}
      </div>
      {sources.length ? (
        <div className="source-list">
          {sources.map((source) => (
            <article key={source.id}>
              <div className="source-file">
                <FileText />
                <div>
                  <span>
                    {source.content_type} ·{" "}
                    {(source.byte_length / 1024).toFixed(1)} KB
                  </span>
                  <h3>{source.original_filename}</h3>
                  <p>SHA-256 {source.sha256.slice(0, 16)}…</p>
                </div>
              </div>
              <div className="source-meta">
                <span>
                  Extraction <b>{source.extraction_status}</b>
                </span>
                <span>
                  Indexing <b>{source.indexing_status}</b>
                </span>
                <span>
                  Created <b>{formatDate(source.created_at)}</b>
                </span>
                <span>
                  Failure <b>{source.failure_code || "None"}</b>
                </span>
              </div>
              {source.failure_message && (
                <div className="notice">
                  <AlertTriangle />
                  <div>
                    <strong>{source.failure_code}</strong>
                    <span>{source.failure_message}</span>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      ) : (
        <Empty
          icon={UploadCloud}
          title="No sources ingested"
          text="Upload a supported document to start the real extraction pipeline."
        />
      )}
      <div className="panel-header">
        <div>
          <h3>Background jobs</h3>
          <p>Durable extraction and indexing status.</p>
        </div>
        <span>{jobs.length} total</span>
      </div>
    </section>
  );
}

function GovernanceView({
  records,
  transition,
}: {
  records: KnowledgeRecord[];
  transition: (item: KnowledgeRecord) => void;
}) {
  const queue = records.filter(
    (item) => !["Published", "Retired"].includes(item.lifecycle),
  );
  return (
    <section className="panel page-panel">
      <PageHeading
        eyebrow="Human authority boundary"
        title="Review and Governance Queue"
        description="Drafts require expert review, approval and publication before retrieval."
      />
      <div className="lifecycle-strip">
        {["Draft", "Review", "Approved", "Published", "Retired"].map(
          (stage, index) => (
            <div key={stage}>
              <b>{index + 1}</b>
              <span>{stage}</span>
              <small>
                {records.filter((item) => item.lifecycle === stage).length}{" "}
                records
              </small>
            </div>
          ),
        )}
      </div>
      {queue.length ? (
        <div className="review-list">
          {queue.map((item) => (
            <article key={item.id}>
              <div>
                <i className={`status-pill ${badge(item.lifecycle)}`}>
                  {item.lifecycle}
                </i>
                <span className="record-id">
                  {item.code} · v{item.current_version}
                </span>
                <h3>{item.title}</h3>
                <p>{item.change_summary}</p>
                <div className="review-meta">
                  <span>
                    Owner <b>{item.owner_user_id}</b>
                  </span>
                  <span>
                    Reviewer <b>{item.reviewer_user_id || "Unassigned"}</b>
                  </span>
                  <span>
                    Scope <b>{item.organization_id}</b>
                  </span>
                </div>
              </div>
              <button
                className="primary-button"
                onClick={() => transition(item)}
              >
                <CheckCircle2 /> Advance lifecycle
              </button>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          icon={CheckCircle2}
          title="Governance queue is clear"
          text="No draft, review or approved records are waiting."
        />
      )}
    </section>
  );
}

function ProviderView({
  status,
  test,
  testing,
}: {
  status: Record<string, unknown>;
  test: () => void;
  testing: boolean;
}) {
  return (
    <section className="panel page-panel">
      <PageHeading
        eyebrow="Server-only provider boundary"
        title="AI Provider Connection"
        description="Gemini credentials remain server-side and are never returned to this interface."
        action={
          <button className="primary-button" onClick={test} disabled={testing}>
            <RefreshCw /> {testing ? "Testing..." : "Run safe test"}
          </button>
        }
      />
      <div className="provider-grid">
        <article>
          <BrainCircuit />
          <span>Provider</span>
          <h3>{String(status.provider || "Gemini")}</h3>
          <p>
            Model execution runs only through the authenticated Core service.
          </p>
        </article>
        <article>
          <KeyRound />
          <span>Configuration</span>
          <h3>
            {status.configured
              ? "API key configured"
              : "API key not configured"}
          </h3>
          <p>Secret value is never rendered or accepted by the browser.</p>
        </article>
        <article>
          <CircleGauge />
          <span>Connection</span>
          <h3>
            {status.connected_and_tested
              ? "Connected and tested"
              : status.configured
                ? "Configured, not tested"
                : "Unavailable"}
          </h3>
          <p>Live AI enabled: {String(Boolean(status.live_ai_enabled))}</p>
        </article>
      </div>
      {Boolean(status.public_static_preview) && (
        <div className="notice">
          <AlertTriangle />
          <div>
            <strong>Static public preview</strong>
            <span>
              {String(
                status.message ||
                  "Live Gemini requires server-enabled hosting.",
              )}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}

function RulesView({
  rules,
  create,
  publish,
}: {
  rules: RuleRecord[];
  create: () => void;
  publish: (rule: RuleRecord) => void;
}) {
  return (
    <section className="panel page-panel">
      <PageHeading
        eyebrow="Governed instruction layer"
        title="AI Rules"
        description="Published rules constrain retrieval and proposed actions for every role."
        action={
          <button className="primary-button" onClick={create}>
            <Plus /> New rule
          </button>
        }
      />
      {rules.length ? (
        <div className="rule-list">
          {rules.map((rule) => (
            <article key={rule.id}>
              <div className="rule-number">{rule.priority}</div>
              <div>
                <span>
                  {rule.code} · v{rule.version}
                </span>
                <h3>{rule.title}</h3>
                <p>{rule.instruction}</p>
              </div>
              <div className="rule-actions">
                <i className={`status-pill ${badge(rule.lifecycle)}`}>
                  {rule.lifecycle}
                </i>
                {rule.lifecycle !== "Published" && (
                  <button
                    className="secondary-button"
                    onClick={() => publish(rule)}
                  >
                    Publish
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          icon={Settings2}
          title="No AI rules"
          text="Create the first governed instruction."
        />
      )}
    </section>
  );
}

function AuditView({
  records,
  title = "Append-only Audit Log",
}: {
  records: AuditRecord[];
  title?: string;
}) {
  return (
    <section className="panel page-panel">
      <PageHeading
        eyebrow="Traceable control plane"
        title={title}
        description="Actions are persisted with verified actor, role, entity, organization and request trace."
      />
      {records.length ? (
        <div className="audit-list">
          {records.map((event) => (
            <article key={event.id}>
              <div className="audit-icon">
                <History />
              </div>
              <div>
                <span>
                  {formatDate(event.created_at)} · {event.actor_role}
                </span>
                <h3>{event.action}</h3>
                <p>
                  {event.entity_type} {event.entity_id}
                </p>
              </div>
              <div>
                <i className="status-pill published">Recorded</i>
                <small>{event.organization_id || "Global"}</small>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          icon={History}
          title="No audit events"
          text="Verified actions will be recorded here."
        />
      )}
    </section>
  );
}

function AccessModelView({
  identities,
  mode,
}: {
  identities: AccessIdentity[];
  mode: "organizations" | "permissions";
}) {
  return (
    <section className="panel page-panel">
      <PageHeading
        eyebrow="Server-verified access boundary"
        title={
          mode === "organizations"
            ? "Organization Isolation"
            : "Roles and Permissions"
        }
        description={
          mode === "organizations"
            ? "The synchronized identity map defines organization and department retrieval scope."
            : "Administration remains exclusive to SYNERGIZE; consumption permissions are role-aware and server enforced."
        }
      />
      {identities.length ? (
        <div className="review-list">
          {identities.map((identity) => (
            <article key={identity.user_id}>
              <div>
                <i
                  className={`status-pill ${identity.role === "synergize_team" ? "published" : "approved"}`}
                >
                  {identity.role.replace("_", " ")}
                </i>
                <span className="record-id">
                  User {identity.user_id} · synchronized{" "}
                  {formatDate(identity.synchronized_at)}
                </span>
                <h3>{identity.display_name}</h3>
                <p>
                  {mode === "organizations"
                    ? `Primary organization: ${identity.organization_id || "Cross-organization authority"} · Assigned: ${identity.assigned_organization_ids.join(", ") || "None"} · Departments: ${identity.department_ids.join(", ") || "All permitted"}`
                    : identity.permissions.join(" · ")}
                </p>
              </div>
              <div className="review-actions">
                <span className="status-pill published">Verified</span>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          icon={UsersRound}
          title="No synchronized identities"
          text="Verified NASAQ identities appear after the authentication bridge exchanges a session."
        />
      )}
    </section>
  );
}

function EventMonitorView({ events }: { events: EventRecord[] }) {
  return (
    <section className="panel page-panel">
      <PageHeading
        eyebrow="Additive NASAQ event bridge"
        title="Event Monitor"
        description="Only whitelisted operational metadata is persisted; events are idempotent, scoped and processed by deterministic monitors."
      />
      {events.length ? (
        <div className="audit-list">
          {events.map((event) => (
            <article key={event.id}>
              <div className="audit-icon">
                <Activity />
              </div>
              <div>
                <span>
                  {formatDate(event.occurred_at)} ·{" "}
                  {event.actor.role || "system"}
                </span>
                <h3>{event.type}</h3>
                <p>
                  {event.entity.type} {event.entity.id} ·{" "}
                  {Object.entries(event.sanitized_snapshot)
                    .map(([key, value]) => `${key}: ${String(value)}`)
                    .join(" · ")}
                </p>
              </div>
              <div>
                <i
                  className={`status-pill ${event.processed_at ? "published" : "review"}`}
                >
                  {event.processed_at ? "Processed" : "Pending"}
                </i>
                <small>Organization {event.organization_id}</small>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          icon={Activity}
          title="No operational events"
          text="Authorized NASAQ outbox events will appear here after delivery."
        />
      )}
    </section>
  );
}

function InsightsView({
  insights,
  act,
}: {
  insights: InsightRecord[];
  act: (
    item: InsightRecord,
    action: "view" | "snooze" | "dismiss" | "accept",
  ) => void;
}) {
  return (
    <section className="panel page-panel">
      <PageHeading
        eyebrow="Authorized third-eye monitoring"
        title="Proactive Insights Queue"
        description="Event-driven alerts are deduplicated, scoped and persisted across devices."
      />
      {insights.length ? (
        <div className="review-list">
          {insights.map((item) => (
            <article key={item.id}>
              <div>
                <i className={`status-pill ${badge(item.priority)}`}>
                  {item.priority}
                </i>
                <span className="record-id">
                  {item.state} · Organization {item.organization_id}
                </span>
                <h3>{item.title}</h3>
                <p>{item.message}</p>
                <div className="review-meta">
                  <span>
                    Reason <b>{item.reason}</b>
                  </span>
                  <span>
                    Responsible <b>{item.responsible}</b>
                  </span>
                  <span>
                    Due <b>{formatDate(item.due_date)}</b>
                  </span>
                </div>
              </div>
              <div className="review-actions">
                <button
                  className="secondary-button"
                  onClick={() => act(item, "snooze")}
                >
                  Snooze
                </button>
                <button
                  className="secondary-button"
                  onClick={() => act(item, "dismiss")}
                >
                  Dismiss
                </button>
                <button
                  className="primary-button"
                  onClick={() => act(item, "accept")}
                >
                  Accept
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          icon={Bell}
          title="No insights in this scope"
          text="Only role- and organization-authorized insights are returned."
        />
      )}
    </section>
  );
}

function ActionsView({
  actions,
  decide,
}: {
  actions: ActionRecord[];
  decide: (item: ActionRecord, decision: "approve" | "reject") => void;
}) {
  return (
    <section className="panel page-panel">
      <PageHeading
        eyebrow="Human approval boundary"
        title="Action Approval Queue"
        description="Assistant proposals remain drafts until an authorized human decides them. Approval records the decision but never mutates NASAQ workflows directly."
      />
      {actions.length ? (
        <div className="review-list">
          {actions.map((item) => (
            <article key={item.id}>
              <div>
                <i className={`status-pill ${badge(item.state)}`}>
                  {item.state}
                </i>
                <span className="record-id">
                  Organization {item.organization_id} · {item.required_approver}
                </span>
                <h3>{item.action_type.replace(/_/g, " ")}</h3>
                <p>{item.proposed_value}</p>
                <div className="review-meta">
                  <span>
                    Reason <b>{item.reason}</b>
                  </span>
                  <span>
                    Expected impact <b>{item.expected_impact}</b>
                  </span>
                  <span>
                    Risk <b>{item.risk}</b>
                  </span>
                </div>
              </div>
              {item.state === "draft" && (
                <div className="review-actions">
                  <button
                    className="secondary-button"
                    onClick={() => decide(item, "reject")}
                  >
                    Reject
                  </button>
                  <button
                    className="primary-button"
                    onClick={() => decide(item, "approve")}
                  >
                    Approve
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      ) : (
        <Empty
          icon={CheckCircle2}
          title="No action proposals"
          text="Grounded assistant proposals will appear here with their evidence and required approver."
        />
      )}
    </section>
  );
}

function EvaluationsView({ records }: { records: EvaluationRecord[] }) {
  return (
    <section className="panel page-panel">
      <PageHeading
        eyebrow="Continuous quality control"
        title="Evaluation Center"
        description="Every assistant response records a persistent citation-integrity result without storing the user prompt in evaluation details."
      />
      {records.length ? (
        <div className="audit-list">
          {records.map((item) => (
            <article key={item.id}>
              <div className="audit-icon">
                <FileCheck2 />
              </div>
              <div>
                <span>
                  {formatDate(item.created_at)} · {item.category}
                </span>
                <h3>{item.name}</h3>
                <p>
                  Trace {item.trace_id || "Not available"} ·{" "}
                  {Object.entries(item.details)
                    .map(([key, value]) => `${key}: ${String(value)}`)
                    .join(" · ")}
                </p>
              </div>
              <div>
                <i className={`status-pill ${badge(item.status)}`}>
                  {item.status}
                </i>
                <small>{Math.round(item.score * 100)}%</small>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          icon={FileCheck2}
          title="No evaluations recorded"
          text="A result is recorded automatically after the next grounded assistant response."
        />
      )}
    </section>
  );
}

function AssistantView({
  conversations,
  active,
  select,
  create,
  send,
  sending,
  feedback,
}: {
  conversations: ConversationRecord[];
  active: ConversationRecord | null;
  select: (id: string) => void;
  create: () => void;
  send: (text: string) => void;
  sending: boolean;
  feedback: (id: string, rating: "helpful" | "incorrect") => void;
}) {
  const [text, setText] = useState("");
  const submit = () => {
    if (!text.trim() || sending) return;
    send(text.trim());
    setText("");
  };
  return (
    <section className="assistant-layout">
      <aside className="panel conversation-list">
        <div className="panel-header">
          <div>
            <h3>Conversations</h3>
            <p>Persisted for this user and role.</p>
          </div>
          <button
            className="icon-button"
            onClick={create}
            aria-label="New conversation"
          >
            <Plus />
          </button>
        </div>
        {conversations.map((item) => (
          <button
            key={item.id}
            className={active?.id === item.id ? "active" : ""}
            onClick={() => select(item.id)}
          >
            <MessageSquareText />
            <div>
              <strong>{item.title}</strong>
              <span>Organization {item.organization_id}</span>
              <small>{formatDate(item.updated_at)}</small>
            </div>
          </button>
        ))}
      </aside>
      <div className="panel assistant-panel">
        <PageHeading
          eyebrow="Verified user · scoped organization"
          title="NASAQ AI Assistant"
          description="Real retrieval, citations, evidence sufficiency and approval-controlled proposals."
        />
        {active?.messages?.length ? (
          <div className="message-stream">
            {active.messages.map((message) => (
              <article
                key={message.id}
                className={
                  message.sender === "user" ? "user-message" : "core-message"
                }
              >
                <div className="message-avatar">
                  {message.sender === "user" ? "YOU" : <Bot />}
                </div>
                <div>
                  <span>
                    {message.sender === "user"
                      ? "You"
                      : "NASAQ Intelligence Core"}{" "}
                    · {formatDate(message.created_at)}
                  </span>
                  <p>{message.content}</p>
                  {message.response_contract && (
                    <ResponseDetails response={message.response_contract} />
                  )}
                  {message.sender === "assistant" && (
                    <div className="message-actions">
                      <button onClick={() => feedback(message.id, "helpful")}>
                        Helpful
                      </button>
                      <button onClick={() => feedback(message.id, "incorrect")}>
                        Incorrect
                      </button>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <Empty
            icon={Bot}
            title="Start a grounded conversation"
            text="Ask about authorized evidence, published knowledge or a current workflow context."
          />
        )}
        <div className="assistant-composer">
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
            placeholder="Ask the NASAQ Intelligence Core…"
          />
          <button
            className="primary-button"
            onClick={submit}
            disabled={!text.trim() || sending}
          >
            <Send /> {sending ? "Grounding…" : "Send"}
          </button>
        </div>
      </div>
    </section>
  );
}

function ResponseDetails({ response }: { response: GroundedResponse }) {
  return (
    <div className="response-details">
      <div className="tag-row">
        <span>Evidence: {response.evidence_status}</span>
        <span>Confidence: {Math.round(response.confidence * 100)}%</span>
        <span>Risk: {response.risk_level}</span>
        {response.requires_approval && <span>Approval required</span>}
      </div>
      {response.citations.length > 0 && (
        <div className="citation-grid">
          {response.citations.map((citation) => (
            <article key={`${citation.source_id}-${citation.section}`}>
              <FileCheck2 />
              <div>
                <strong>{citation.title}</strong>
                <span>
                  {citation.source_id} · {citation.section || "Source"}
                  {citation.page ? ` · p.${citation.page}` : ""}
                </span>
                <small>{citation.knowledge_version}</small>
              </div>
            </article>
          ))}
        </div>
      )}
      {response.missing_information.length > 0 && (
        <div className="notice">
          <AlertTriangle />
          <div>
            <strong>Missing information</strong>
            <span>{response.missing_information.join(" · ")}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ModalShell({
  title,
  close,
  children,
}: {
  title: string;
  close: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && close()}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <header>
          <h2>{title}</h2>
          <button className="icon-button" onClick={close} aria-label="Close">
            <X />
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

function KnowledgeModal({
  close,
  submit,
  organizationId,
  busy,
  initial,
}: {
  close: () => void;
  submit: (value: Record<string, unknown>) => void;
  organizationId: string;
  busy: boolean;
  initial?: KnowledgeRecord | null;
}) {
  const [title, setTitle] = useState(initial?.title || "");
  const [content, setContent] = useState(initial?.content || "");
  const [category, setCategory] = useState(initial?.category || "Methodology");
  return (
    <ModalShell
      title={
        initial
          ? "Create governed knowledge version"
          : "Create governed knowledge"
      }
      close={close}
    >
      <div className="form-grid two">
        <label>
          Title
          <input value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label>
          Category
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {[
              "Methodology",
              "Playbooks",
              "Cases",
              "Templates",
              "Expert Decisions",
              "Market Context",
            ].map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </label>
        <label className="full-field">
          Knowledge content
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        </label>
      </div>
      <div className="notice">
        <ShieldCheck />
        <div>
          <strong>Draft by default</strong>
          <span>
            {initial
              ? "The new version preserves history and returns to Draft until it is reviewed, approved and published."
              : "This record remains unavailable to retrieval until reviewed, approved and published."}
          </span>
        </div>
      </div>
      <footer className="modal-actions">
        <button className="secondary-button" onClick={close}>
          Cancel
        </button>
        <button
          className="primary-button"
          disabled={busy || title.length < 3 || content.length < 20}
          onClick={() =>
            submit({
              title,
              category,
              organization_id: initial?.organization_id || organizationId,
              department_id: initial?.department_id || null,
              confidentiality:
                initial?.confidentiality || "client_confidential",
              owner_user_id: initial?.owner_user_id || "1",
              applicability:
                initial?.applicability || `Organization ${organizationId}`,
              tags: initial?.tags || ["manual"],
              change_summary: initial
                ? `Governed update from version ${initial.current_version}`
                : "Initial governed manual entry",
              content,
              author_user_id: "1",
            })
          }
        >
          {initial ? <FileCheck2 /> : <Plus />}{" "}
          {initial ? "Create Draft version" : "Create Draft"}
        </button>
      </footer>
    </ModalShell>
  );
}

function SourceModal({
  close,
  submit,
  organizationId,
  busy,
}: {
  close: () => void;
  submit: (file: File, metadata: Record<string, unknown>) => void;
  organizationId: string;
  busy: boolean;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  return (
    <ModalShell title="Ingest governed source" close={close}>
      <div className="upload-zone">
        <UploadCloud />
        <h3>Choose a real source file</h3>
        <p>
          PDF, DOCX, XLSX, CSV, TXT or Markdown. The original is stored and
          processed server-side.
        </p>
        <label className="primary-button">
          Choose file
          <input
            type="file"
            accept=".pdf,.docx,.xlsx,.csv,.txt,.md"
            onChange={(e) => {
              const next = e.target.files?.[0] || null;
              setFile(next);
              setTitle(next?.name.replace(/\.[^.]+$/, "") || "");
            }}
          />
        </label>
        {file && (
          <span>
            {file.name} · {(file.size / 1024).toFixed(1)} KB
          </span>
        )}
      </div>
      <div className="form-grid two">
        <label>
          Knowledge title
          <input value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label>
          Organization
          <input value={organizationId} readOnly />
        </label>
      </div>
      <footer className="modal-actions">
        <button className="secondary-button" onClick={close}>
          Cancel
        </button>
        <button
          className="primary-button"
          disabled={busy || !file || title.length < 3}
          onClick={() =>
            file &&
            submit(file, {
              title,
              category: "Methodology",
              organization_id: organizationId,
              department_id: null,
              confidentiality: "client_confidential",
              owner_user_id: "1",
              applicability: `Organization ${organizationId}`,
              tags: ["uploaded"],
              change_summary: "Initial source ingestion",
            })
          }
        >
          <UploadCloud /> Upload and process
        </button>
      </footer>
    </ModalShell>
  );
}

export function App() {
  const [role, setRole] = useState<Role>("synergize_team");
  const [view, setView] = useState<View>("overview");
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("nasaq-core-sidebar") === "collapsed",
  );
  const [locale, setLocale] = useState<Locale>(() =>
    localStorage.getItem("nasaq-core-language") === "ar" ? "ar" : "en",
  );
  const [organizationId, setOrganizationIdState] = useState("1");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [modal, setModal] = useState<Modal>(null);
  const [editing, setEditing] = useState<KnowledgeRecord | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [metrics, setMetrics] = useState<Record<string, number>>({});
  const [knowledge, setKnowledge] = useState<KnowledgeRecord[]>([]);
  const [sources, setSources] = useState<SourceRecord[]>([]);
  const [rules, setRules] = useState<RuleRecord[]>([]);
  const [audit, setAudit] = useState<AuditRecord[]>([]);
  const [jobs, setJobs] = useState<Array<Record<string, unknown>>>([]);
  const [provider, setProvider] = useState<Record<string, unknown>>({});
  const [conversations, setConversations] = useState<ConversationRecord[]>([]);
  const [active, setActive] = useState<ConversationRecord | null>(null);
  const [insights, setInsights] = useState<InsightRecord[]>([]);
  const [actions, setActions] = useState<ActionRecord[]>([]);
  const [evaluations, setEvaluations] = useState<EvaluationRecord[]>([]);
  const [identities, setIdentities] = useState<AccessIdentity[]>([]);
  const [events, setEvents] = useState<EventRecord[]>([]);
  const loaded = useRef(false);
  const admin = role === "synergize_team";
  const safely = useCallback(async (work: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await work();
    } catch (cause) {
      setError(
        cause instanceof CoreApiError
          ? `${cause.message} (${cause.code})`
          : cause instanceof Error
            ? cause.message
            : "Unexpected Core error.",
      );
    } finally {
      setBusy(false);
    }
  }, []);
  const load = useCallback(async () => {
    await safely(async () => {
      const [knowledgeData, conversationData, insightData, providerData] =
        await Promise.all([
          api.knowledge(search),
          api.conversations(),
          api.insights(),
          api.provider(),
        ]);
      setKnowledge(knowledgeData.items);
      setConversations(conversationData.items);
      setInsights(insightData.items);
      setProvider(providerData);
      if (admin) {
        const [
          dashboard,
          sourcesData,
          rulesData,
          auditData,
          jobsData,
          actionData,
          evaluationData,
          accessData,
          eventData,
        ] = await Promise.all([
          api.dashboard(),
          api.sources(),
          api.rules(),
          api.audit(),
          api.jobs(),
          api.actions(),
          api.evaluations(),
          api.accessModel(),
          api.events(),
        ]);
        setMetrics(dashboard);
        setSources(sourcesData.items);
        setRules(rulesData.items);
        setAudit(auditData.items);
        setJobs(jobsData.items);
        setActions(actionData.items);
        setEvaluations(evaluationData.items);
        setIdentities(accessData.identities);
        setEvents(eventData.items);
      }
      if (active?.id) {
        const detail = await api.conversation(active.id);
        setActive(detail.conversation);
      }
    });
  }, [active?.id, admin, safely, search]);
  useEffect(() => {
    const profile = roles.find((item) => item.id === role)!;
    setSessionReady(false);
    void safely(async () => {
      const identity = await api.startDemoSession(profile.user);
      const org =
        identity.organization_id ||
        identity.assigned_organization_ids[0] ||
        "1";
      api.setOrganization(org);
      setOrganizationIdState(org);
      setActive(null);
      loaded.current = true;
      await load();
      setSessionReady(true);
    });
  }, [role]);
  useEffect(() => {
    if (!admin) setView("assistant");
  }, [admin]);
  useEffect(() => {
    localStorage.setItem(
      "nasaq-core-sidebar",
      collapsed ? "collapsed" : "expanded",
    );
  }, [collapsed]);
  useEffect(() => {
    localStorage.setItem("nasaq-core-language", locale);
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
  }, [locale]);
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [view]);
  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        document.getElementById("core-global-search")?.focus();
      }
    };
    window.addEventListener("keydown", focusSearch);
    return () => window.removeEventListener("keydown", focusSearch);
  }, []);
  useEffect(() => {
    if (!loaded.current) return;
    api.setOrganization(organizationId);
    setActive(null);
    void load();
  }, [organizationId]);
  useEffect(() => {
    if (!loaded.current) return;
    const timer = setTimeout(() => void load(), 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    const controller = new AbortController();
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    if (sessionReady)
      void api
        .subscribe(() => {
          if (refreshTimer) clearTimeout(refreshTimer);
          refreshTimer = setTimeout(() => void load(), 250);
        }, controller.signal)
        .catch(() => undefined);
    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      controller.abort();
    };
  }, [role, organizationId, sessionReady]);
  const setOrganizationId = (id: string) => {
    api.setOrganization(id);
    setOrganizationIdState(id);
  };
  const transition = (item: KnowledgeRecord) =>
    void safely(async () => {
      const actions: Record<
        string,
        "submit-review" | "approve" | "publish" | "retire"
      > = {
        Draft: "submit-review",
        Review: "approve",
        Approved: "publish",
        Published: "retire",
      };
      const action = actions[item.lifecycle];
      if (!action) return;
      await api.transitionKnowledge(
        item.id,
        action,
        `${action} confirmed by verified ${role}`,
      );
      await load();
    });
  const selectConversation = (id: string) =>
    void safely(async () => {
      const result = await api.conversation(id);
      setActive(result.conversation);
    });
  const createConversation = () =>
    void safely(async () => {
      const result = await api.createConversation(
        `Organization ${organizationId} guidance`,
      );
      setActive({ ...result.conversation, messages: [] });
      await load();
    });
  const send = (message: string) =>
    void safely(async () => {
      const result = await api.assist({
        conversation_id: active?.id,
        context: {
          page: "Intelligence Core",
          route: `/${view}`,
          entity_type: "other",
          workflow_stage: view,
          authorized_evidence_refs: [],
        },
        message,
        mode: "ask",
      });
      const detail = await api.conversation(result.conversation_id);
      setActive(detail.conversation);
      await load();
    });
  const actInsight = (
    item: InsightRecord,
    action: "view" | "snooze" | "dismiss" | "accept",
  ) =>
    void safely(async () => {
      await api.insight(item.id, action);
      await load();
      if (action === "accept" && item.deep_link)
        window.location.href = item.deep_link;
    });
  const decideAction = (item: ActionRecord, decision: "approve" | "reject") =>
    void safely(async () => {
      await api.decideAction(item.id, decision);
      await load();
    });
  const content = useMemo(() => {
    if (!admin)
      return (
        <AssistantView
          conversations={conversations}
          active={active}
          select={selectConversation}
          create={createConversation}
          send={send}
          sending={busy}
          feedback={(id, rating) =>
            void safely(async () => {
              await api.feedback(id, rating);
            })
          }
        />
      );
    switch (view) {
      case "overview":
        return (
          <Overview
            metrics={metrics}
            knowledge={knowledge}
            insights={insights}
            setView={setView}
          />
        );
      case "library":
        return (
          <KnowledgeView
            records={knowledge}
            search={search}
            transition={transition}
            openCreate={() => setModal("knowledge")}
            edit={(item) => {
              setEditing(item);
              setModal("edit");
            }}
          />
        );
      case "sources":
        return (
          <SourcesView
            sources={sources}
            jobs={jobs}
            upload={() => setModal("source")}
          />
        );
      case "governance":
        return <GovernanceView records={knowledge} transition={transition} />;
      case "provider":
        return (
          <ProviderView
            status={provider}
            testing={busy}
            test={() =>
              void safely(async () => {
                setProvider(await api.testProvider());
              })
            }
          />
        );
      case "rules":
        return (
          <RulesView
            rules={rules}
            create={() => {
              const title = prompt("Rule title");
              const instruction = prompt("Approved instruction");
              if (title && instruction)
                void safely(async () => {
                  await api.createRule({
                    title,
                    instruction,
                    priority: rules.length + 1,
                    applies_to: ["all"],
                  });
                  await load();
                });
            }}
            publish={(rule) =>
              void safely(async () => {
                await api.updateRule(rule.id, { lifecycle: "Published" });
                await load();
              })
            }
          />
        );
      case "audit":
        return <AuditView records={audit} />;
      case "insights":
        return <InsightsView insights={insights} act={actInsight} />;
      case "assistant":
        return (
          <AssistantView
            conversations={conversations}
            active={active}
            select={selectConversation}
            create={createConversation}
            send={send}
            sending={busy}
            feedback={(id, rating) =>
              void safely(async () => {
                await api.feedback(id, rating);
              })
            }
          />
        );
      case "isolation":
        return <AccessModelView identities={identities} mode="organizations" />;
      case "permissions":
        return <AccessModelView identities={identities} mode="permissions" />;
      case "integration":
        return <EventMonitorView events={events} />;
      case "events":
        return <EventMonitorView events={events} />;
      case "evaluations":
        return <EvaluationsView records={evaluations} />;
      case "actions":
        return <ActionsView actions={actions} decide={decideAction} />;
      default:
        return (
          <Overview
            metrics={metrics}
            knowledge={knowledge}
            insights={insights}
            setView={setView}
          />
        );
    }
  }, [
    view,
    admin,
    metrics,
    knowledge,
    insights,
    search,
    sources,
    jobs,
    provider,
    rules,
    audit,
    conversations,
    active,
    busy,
    actions,
    evaluations,
    identities,
    events,
  ]);
  const activeProfile = roles.find((item) => item.id === role);
  return (
    <LocaleContext.Provider value={locale}>
      <div
        className={`app-shell locale-${locale}`}
        dir={locale === "ar" ? "rtl" : "ltr"}
      >
        <Sidebar
          view={view}
          setView={setView}
          role={role}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          locale={locale}
        />
        <div className="app-main">
          <Header
            role={role}
            setRole={setRole}
            organizationId={organizationId}
            setOrganizationId={setOrganizationId}
            search={search}
            setSearch={setSearch}
            openSearch={() => setView("insights")}
            refresh={() => void load()}
            busy={busy}
            locale={locale}
            setLocale={setLocale}
            insightCount={insights.length}
          />
          <main className="content-shell">
            {error && <Notice error={error} clear={() => setError("")} />}
            <div className="environment-strip">
              <span>
                <Database />{" "}
                {locale === "ar"
                  ? "بيانات خادم مستمرة"
                  : "Persistent server data"}
              </span>
              <span>
                <ShieldCheck />{" "}
                {locale === "ar"
                  ? `الدور الموثق: ${activeProfile?.labelAr}`
                  : `Verified role: ${activeProfile?.label}`}
              </span>
              <span>
                <Fingerprint />{" "}
                {locale === "ar"
                  ? `المؤسسة ${organizationId}`
                  : `Organization ${organizationId}`}
              </span>
              <span>
                <BrainCircuit />{" "}
                {provider.configured
                  ? locale === "ar"
                    ? "Gemini مهيأ"
                    : "Gemini configured"
                  : locale === "ar"
                    ? "المزوّد غير متاح"
                    : "Provider unavailable"}
              </span>
            </div>
            {content}
          </main>
        </div>
        {modal === "knowledge" && (
          <KnowledgeModal
            close={() => setModal(null)}
            organizationId={organizationId}
            busy={busy}
            submit={(value) =>
              void safely(async () => {
                await api.createKnowledge(value);
                setModal(null);
                await load();
              })
            }
          />
        )}{" "}
        {modal === "edit" && editing && (
          <KnowledgeModal
            initial={editing}
            close={() => {
              setModal(null);
              setEditing(null);
            }}
            organizationId={organizationId}
            busy={busy}
            submit={(value) =>
              void safely(async () => {
                await api.updateKnowledge(editing.id, value);
                setModal(null);
                setEditing(null);
                await load();
              })
            }
          />
        )}{" "}
        {modal === "source" && (
          <SourceModal
            close={() => setModal(null)}
            organizationId={organizationId}
            busy={busy}
            submit={(file, metadata) =>
              void safely(async () => {
                await api.ingest(file, metadata);
                setModal(null);
                await load();
              })
            }
          />
        )}
        <button
          className="floating-core"
          onClick={() => setView("assistant")}
          aria-label={
            locale === "ar" ? "فتح مساعد نسق الذكي" : "Open NASAQ AI Assistant"
          }
        >
          <Bot />
          {insights.length > 0 && <span>{insights.length}</span>}
        </button>
      </div>
    </LocaleContext.Provider>
  );
}
